import { useRouter } from '@abyss/web/hooks/useRouter';
import { useToast } from '@abyss/web/hooks/useToast';
import { storage } from '@abyss/web/tools/storage';
import { Drawer } from '@abyss/web/ui/Drawer';
import { IconBrand } from '@abyss/web/ui/IconBrand';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Markdown } from '@abyss/web/ui/Markdown';
import { Text } from '@abyss/web/ui/Text';
import { Tooltip } from '@abyss/web/ui/Tooltip';
import { t } from 'i18next';
import { find } from 'lodash';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { ScheduleModal as AscensionScheduleModal } from '../../common/ScheduleModal/ScheduleModal';
import { PortalContext } from '../../context/PortalContext';
import { useLagoon } from '../../hooks/useLagoon';
import { usePrimaryCare } from '../../hooks/usePrimaryCare';
import { useRemoveCareTeamProvider } from '../../utils/careTeamRemoval.utils';
import { SnackCardProvider } from '../../hooks/useSnackCardProviderSearch/useSnackCardProviderSearch';
import { useSpecialtiesResult } from '../../hooks/useSpecialties';
import { BreadcrumbSessionStorage } from '../../models/BreadcrumbSessionStorage';
import { BhProviderIcons } from '../../models/Lagoon';
import { ProviderType } from '../../models/ProviderDetails';
import { useCareTeamStore } from '../../store/useCareTeamStore';
import { CareTeamState } from '../../store/useCareTeamStore/careTeamStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../store/useDetailsStore';
import { DetailsStore } from '../../store/useDetailsStore/detailsStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { usePlanSelectorStore } from '../../store/usePlanSelectorStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { useNonGatedPcpStore } from '../../store/useNonGatedPcpStore';
import { NonGatedPcpData } from '../../store/useNonGatedPcpStore/nonGatedPcpStore';
import { getSpecialityTitle } from '../../utils/card.utils';
import {
  checkAscensionScheduleDataIndicator,
  getPlanIndicator,
} from '../../utils/common';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { getBhIcon } from '../../utils/iconography.utils';
import { doChoosePCPStoreUpdate } from '../../utils/primaryCare.utils';
import { getProviderSpecialtyOrOrgTypeCode } from '../../utils/providerDetails.utils';
import {
  getNameAttrs,
  parseProviderName,
} from '../../utils/providerNames.utils';
import { isPcpSuppressionEligible } from '../../utils/pcpSuppression.utils';
import { getCurrentMember } from '../../utils/user.utils';
import { AdobeConstantsAscensionSchedule } from '../AdobeTagging/adobeConstants';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../AdobeTagging/adobeModalTrackEvent';
import { ProviderDetailsLoader } from '../BookmarkButton/Utils';
import {
  ANALYTICS_CONSTANTS,
  OutOfNetworkBadge,
  getIsSuperUser,
  getStaffModelGoingOON,
} from '../CareTeamCard/CareTeamUtils';
import {
  CareCategories,
  Constants,
  PESNetworkStatus,
  ProviderTypes,
} from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { ConstantsRoutes } from '../ConstantsRoutes';
import { NetworkWrapper } from '../DataCard/DataCard.style';
import { IconBrandAndSymbol } from '../IconBrandAndSymbol';
import { IconName } from '../IconBrandAndSymbol/types';
import { ImageComponent } from '../ImageComponent';
import { LinkWithTracking } from '../LinkWithTracking/LinkWithTracking';
import { PortalIdentifier } from '../Portal';
import { toastSuccessStyle } from '../ShareThisProvider/ShareThisProvider.style';
import { SnackDescription } from '../SnackCard/SnackCard.styles';
import { ThingsToConsiderModal } from '../ThingsToConsiderModal';
import { convertObjectToUrlParams, getFeatureFlag } from '../Utils';
import { isMnrMember } from '../Utils/memberUtils/memberUtils';
import { checkTerminationDate } from '../Utils/terminateDateIndicatorUtils';
import { truncateLargeStrings } from '../Utils/truncateLargeStrings';
import { CallModal } from './CallModal';
import {
  Avatar,
  CTAContainer,
  HiddenCtaDiv,
  HiddenSpecialityDiv,
  LearMoreLink,
  LearnMoreDrawer,
  ProviderCardContainer,
  ProviderInfoCard,
  SnackContainer,
} from './CareTeamProviderCard.styled';
import {
  getBhIconValue,
  getDisableAllCtas,
  getEffectiveNetworkStatus,
  getIsOnlineProvider,
  getNetworkStatusIcon,
  getProviderDetailsPath,
  getSectionType,
} from './CareTeamProviderCard.utils';
import { CTAButton, VerticalDivider } from './CTAButton';
import { NetworkStatusBadge } from './NetworkStatusBadge';
import { ProviderCardHeader } from './ProviderCardHeader';
import { ScheduleModal } from './ScheduleModal';
import { UndoAction } from './UndoAction';

const getExternalPlanMemberStatus = (
  isKelseySeyboldPlan: boolean,
  isSanitasPlan: boolean,
  pcpInfo: any,
  isFuturePcpCard: boolean,
  sanitasPCP: string
) => {
  const isKelseySeyboldPlanMember =
    isKelseySeyboldPlan &&
    (isFuturePcpCard
      ? pcpInfo?.futurePCP?.providerType?.toLowerCase() ===
        ProviderTypes.MEDICAL_GROUP.toLowerCase()
      : pcpInfo?.providerType?.toLowerCase() ===
        ProviderTypes.MEDICAL_GROUP.toLowerCase());

  const isSanitasPlanMember =
    isSanitasPlan &&
    (isFuturePcpCard
      ? pcpInfo?.futurePCP?.pcpID === sanitasPCP
      : pcpInfo?.pcpID === sanitasPCP);

  return {
    isKelseySeyboldPlanMember,
    isSanitasPlanMember,
    isExternalPlanMember: isKelseySeyboldPlanMember || isSanitasPlanMember,
  };
};

const getNetworkStatusInfo = (
  snackCardProvider: any,
  isFuturePcpCard: boolean,
  pcpInfo: any,
  isStaffModel?: boolean,
  isStaffModelPCPInNetwork?: boolean,
  nonGatedPcpProviderId?: string | null
) => {
  const networkStatus =
    snackCardProvider?.providerLocations?.[0]?.network?.status;
  const providerLocations = snackCardProvider?.providerLocations;
  const location =
    providerLocations && providerLocations.length > 0
      ? providerLocations[0]?.network
      : undefined;

  const isOutOfNetwork =
    location?.status === 'OON' || location?.status === 'NOT_FOUND' || !location;

  const isOON = networkStatus === 'OON';
  const isSuppressed = networkStatus === PESNetworkStatus.SUPPRESSED;
  const isOutOfDirectory = isStaffModel
    ? isStaffModelPCPInNetwork !== undefined && !isStaffModelPCPInNetwork
    : isFuturePcpCard
    ? !pcpInfo?.futurePCP?.providerID
    : !pcpInfo?.providerID && !nonGatedPcpProviderId;

  return {
    networkStatus,
    providerLocations,
    location,
    isOutOfNetwork,
    isOON,
    isSuppressed,
    isOutOfDirectory,
  };
};

export type CareTeamProviderCardProps = {
  coverageType?: string;
  chipValue?: string;
  providerType: ProviderType;
  isFixes?: boolean;
  imgSrc?: string;
  providerId?: string;
  providerName?: string;
  professionalDesignation?: string;
  snackCardProvider?: SnackCardProvider | any;
  organizationType?: string[];
  speciality?: string;
  networkStartDate?: string;
  networkEndDate?: string;
  networkStatus?: Boolean;
  locationId?: string;
  organizationCode?: string[];
  llmCodes?: string;
  searchRadius?: number;
  isTieredPlan?: boolean;
  isTieredProvider?: boolean;
  isPcP?: boolean;
  dependentSeqNbr?: string;
  showPcpLocation?: boolean;
  pcpHasSingleLocation?: boolean;
  isPreEffective?: boolean;
  removeCallback?: () => void;
  futurePCP?: string;
  originalChipValue?: string;
  isFuturePcpCard?: boolean;
  pcpEffectiveStartDate?: string;
  pcpEffectiveEndDate?: string;
  showEffectiveStartDate?: boolean;
  showEffectiveEndDate?: boolean;
  pcpConfig?: any;
  isStaffModelMember?: boolean;
  isStaffModelPCPInNetwork?: boolean;
};

const isPcpAndExternalPlanmemberAndNetworkStatus = (
  isNetworkStatus: boolean,
  isPcP: boolean,
  isKelseySeyboldPlanMember: boolean,
  isSanitasPlanMember: boolean,
  isFuturePcpCard?: boolean
): boolean =>
  isNetworkStatus &&
  !isPcpAndExternalPlanmember(
    isPcP || !!isFuturePcpCard,
    isKelseySeyboldPlanMember,
    isSanitasPlanMember
  );

export const sortProviderLocationsByDistance = (provider) => ({
  ...provider,
  providerLocations: provider.providerLocations
    ? provider.providerLocations.sort(
        (a, b) => parseFloat(a.distance) - parseFloat(b.distance)
      )
    : [],
});

export const LearnMoreHyperLink = ({
  onLearnMoreClick,
}: {
  onLearnMoreClick: () => void;
}) => (
  <LearMoreLink
    data-testid="learn-more-link"
    href="#"
    onClick={(e) => {
      e.stopPropagation();
      onLearnMoreClick();
    }}
  >
    {t('DATA_CARD.LEARN_MORE')}
  </LearMoreLink>
);

const renderCtaButtons = ({
  isPcP,
  isFuturePcpCard,
  isExternalPlanMember,
  isKelseySeyboldPlanMember,
  isSanitasPlanMember,
  displayName,
  kelseySeyboldClinicName,
  sanitasClinicName,
  isNetworkStatus,
  navigateToSnackDetails,
  isTieredProvider,
  isOnlineProvider,
  disableAllCtas,
  providerDetailsMiles,
  setProviderDetails,
  snackCardProvider,
  navigate,
  ConstantsRoutes,
  providerDetailsCall,
  setIsOpen,
  isDocAsapOrScheduleIndicator,
  providerDetailsSchedule,
  onScheduleClick,
  t,
  pcpConfig,
  isStaffModel,
}: any) => {
  // Staff model PCP/Future PCP: hide call and directions buttons
  const isStaffModelPcp = isStaffModel && (isPcP || isFuturePcpCard);
  if (isStaffModelPcp) {
    return <HiddenCtaDiv />;
  }

  // External plan members (Kelsey Seybold or Sanitas)
  if (
    isPcpAndExternalPlanmember(
      isPcP,
      isKelseySeyboldPlanMember,
      isSanitasPlanMember
    ) ||
    (isFuturePcpCard && isExternalPlanMember)
  ) {
    // Get website URL from config if available, otherwise fallback to lagoon
    const getWebsiteUrl = () => {
      if (isFuturePcpCard && pcpConfig?.futurePCPLink) {
        return pcpConfig.futurePCPLink;
      }
      if (!isFuturePcpCard && pcpConfig?.currentPCPLink) {
        return pcpConfig.currentPCPLink;
      }
      // Fallback to lagoon URLs
      if (displayName && isKelseySeyboldPlanMember) {
        return kelseySeyboldClinicName?.url;
      }
      if (displayName && isSanitasPlanMember) {
        return sanitasClinicName?.url;
      }
      return null;
    };

    const websiteUrl = getWebsiteUrl();
    const newTabIcon = !!(isFuturePcpCard
      ? pcpConfig?.futurePCPLink
      : pcpConfig?.currentPCPLink);

    return (
      <CTAButton
        autoTestId={'external-provider-website'}
        data-testid={'external-provider-website'}
        icon="language"
        label={t('Website')}
        newTabIcon={newTabIcon ? 'open_in_new' : undefined}
        onClick={() => {
          if (websiteUrl) {
            window.open(websiteUrl, '_blank');
          } else if (isNetworkStatus) {
            navigateToSnackDetails(false, isTieredProvider);
          }
        }}
      />
    );
  }

  // Future PCP: Only show Directions
  if (isFuturePcpCard) {
    return (
      <>
        {!isOnlineProvider && (
          <CTAButton
            autoTestId={providerDetailsMiles}
            data-testid={providerDetailsMiles}
            disabled={disableAllCtas}
            icon="directions"
            label={t('Directions')}
            networkStatus={!isNetworkStatus}
            onClick={() => {
              if (!disableAllCtas) {
                setProviderDetails(snackCardProvider);
                navigate(ConstantsRoutes.MAP_VIEW.path);
              }
            }}
          />
        )}
      </>
    );
  }

  // Current PCP: Show Call, Schedule, and Directions
  if (isPcP) {
    return (
      <>
        <CTAButton
          autoTestId={providerDetailsCall}
          data-testid={providerDetailsCall}
          disabled={disableAllCtas}
          icon="call"
          label={t('Call')}
          networkStatus={!isNetworkStatus}
          onClick={() => {
            if (!disableAllCtas) {
              setIsOpen(true);
            }
          }}
        />
        {isDocAsapOrScheduleIndicator && (
          <>
            <VerticalDivider />
            <CTAButton
              autoTestId={providerDetailsSchedule}
              data-testid={providerDetailsSchedule}
              disabled={disableAllCtas}
              icon="insert_invitation"
              label={t('DATA_CARD.SCHEDULE')}
              networkStatus={!isNetworkStatus}
              onClick={() => {
                if (!disableAllCtas) {
                  onScheduleClick();
                }
              }}
            />
          </>
        )}
        {!isOnlineProvider && (
          <>
            <VerticalDivider />
            <CTAButton
              autoTestId={providerDetailsMiles}
              data-testid={providerDetailsMiles}
              disabled={disableAllCtas}
              icon="directions"
              label={t('Directions')}
              networkStatus={!isNetworkStatus}
              onClick={() => {
                if (!disableAllCtas) {
                  setProviderDetails(snackCardProvider);
                  navigate(ConstantsRoutes.MAP_VIEW.path);
                }
              }}
            />
          </>
        )}
      </>
    );
  }

  // Regular provider in network
  if (isNetworkStatus) {
    return (
      <>
        <CTAButton
          autoTestId={providerDetailsCall}
          data-testid={providerDetailsCall}
          icon="call"
          label={t('Call')}
          networkStatus={!isNetworkStatus}
          onClick={() => {
            setIsOpen(true);
          }}
        />
        {isDocAsapOrScheduleIndicator && (
          <>
            <VerticalDivider />
            <CTAButton
              autoTestId={providerDetailsSchedule}
              data-testid={providerDetailsSchedule}
              icon="insert_invitation"
              label={t('DATA_CARD.SCHEDULE')}
              networkStatus={!isNetworkStatus}
              onClick={onScheduleClick}
            />
          </>
        )}
        {!isOnlineProvider && (
          <>
            <VerticalDivider />
            <CTAButton
              autoTestId={providerDetailsMiles}
              data-testid={providerDetailsMiles}
              icon="directions"
              label={t('Directions')}
              networkStatus={!isNetworkStatus}
              onClick={() => {
                setProviderDetails(snackCardProvider);
                navigate(ConstantsRoutes.MAP_VIEW.path);
              }}
            />
          </>
        )}
      </>
    );
  }

  return <HiddenCtaDiv />;
};

export const CareTeamProviderCard = ({
  providerType,
  imgSrc,
  providerId,
  providerName,
  professionalDesignation,
  snackCardProvider,
  organizationType,
  speciality,
  coverageType,
  locationId,
  llmCodes,
  organizationCode,
  searchRadius,
  isTieredPlan,
  isTieredProvider,
  isPcP = false,
  dependentSeqNbr,
  showPcpLocation = false,
  pcpHasSingleLocation = false,
  isPreEffective,
  chipValue = '',
  removeCallback,
  futurePCP,
  originalChipValue = '',
  isFuturePcpCard = false,
  pcpEffectiveStartDate,
  pcpEffectiveEndDate,
  showEffectiveStartDate = false,
  showEffectiveEndDate = false,
  pcpConfig,
  isStaffModelMember = false,
  isStaffModelPCPInNetwork,
}: CareTeamProviderCardProps) => {
  snackCardProvider = sortProviderLocationsByDistance(snackCardProvider);
  const isNetworkStatus =
    snackCardProvider?.providerLocations?.[0]?.network?.status === 'INN';
  const ascensionSchedulingLink = snackCardProvider?.providerLocations?.find(
    (location) => checkAscensionScheduleDataIndicator(location, coverageType)
  )?.ascensionSchedulingLink;
  const phone = snackCardProvider?.providerLocations?.[0]?.phones?.phone?.[0];
  const practiceId =
    snackCardProvider?.providerLocations?.[0]?.docasapPracticeId;

  const allConfigs = useLagoon('config');
  const isSuperUser = getIsSuperUser(allConfigs);
  const imgRef = useRef<HTMLImageElement>(null);
  const { portalData } = useContext(PortalContext);
  const featureFlags = useLagoon('feature-flags')();
  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;
  const { SELECTED_PROVIDER_ID } = Constants.STORAGE_KEYS.SESSION;
  const additionalIconCss = React.useMemo(
    () => (isGehaTheme ? { css: { left: 0 } } : {}),
    [isGehaTheme]
  );
  const nameAttrs = getNameAttrs(snackCardProvider);
  const ascensionIndicatorEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ASCENSION_INDICATOR_ENABLE
  );
  const enableStaffModel = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ALL_STAFF_MODEL_MNR
  );
  const enableStaffModelEni = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ALL_STAFF_MODEL_ENI
  );
  const enableNonGatedPcpSuppression = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NON_GATED_PCP_SUPPRESSION
  );
  const ascensionAnalyticsEnabled = React.useMemo(
    () =>
      getFeatureFlag(
        featureFlags,
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_ASCENSION_ANALYTICS
      ),
    [featureFlags]
  );
  const isAscensionIndicator = (() => {
    if (!ascensionIndicatorEnabled) return false;

    return snackCardProvider?.providerLocations?.some((location) =>
      checkAscensionScheduleDataIndicator(location, coverageType)
    );
  })();

  const { setProviderDetails } = useCareTeamStore(
    useShallow((state: CareTeamState) => state)
  );

  const { setPCPSearchState } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      setPCPSearchState: state.setPCPSearchState,
    }))
  );

  const currentMember = getCurrentMember();

  const { nonGatedPrimaryCareData } = useNonGatedPcpStore(
    useShallow((state) => ({
      nonGatedPrimaryCareData: state.nonGatedPrimaryCareData,
    }))
  );

  const isPcpSuppressed = isPcpSuppressionEligible(
    currentMember,
    enableNonGatedPcpSuppression
  );
  const nonGatedPcpProviderId = isPcpSuppressed
    ? (nonGatedPrimaryCareData as NonGatedPcpData | null)?.providerID
    : null;
  const nonGatedPcpProviderName = isPcpSuppressed
    ? (nonGatedPrimaryCareData as NonGatedPcpData | null)?.providerName
    : null;

  const bhProviderIcons: BhProviderIcons[] = useLagoon('bh_provider_icons')();
  const schedulingEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ONLINE_SCHEDULING
  );
  const doShowDocASAP =
    schedulingEnabled &&
    snackCardProvider?.providerLocations?.some(
      (location) => !!location?.docasapPracticeId
    );

  const isDocAsapOrScheduleIndicator = doShowDocASAP || isAscensionIndicator;

  const { t } = useTranslation();
  const { navigate } = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const { toast } = useToast();
  const [fetchDetails, setFetchDetails] = useState(false);
  const planInfo = usePlanSelectorStore((state) => state.planInfo);
  const uiMessaging = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();

  const data = useLagoon('ui-messaging')();
  const removeToastContent = find(data, {
    key: ConstantsLagoon.REMOVE_PROVIDER_TOAST.REMOVE_PROVIDER_TOAST_MESSAGE,
  });
  const learnMoreDrawerTitle = find(data, {
    key: ConstantsLagoon.UI_MESSAGING.LEARN_MORE_HYPERLINK_DRAWER_TITLE,
  });
  const learnMoreDrawerDescription = find(data, {
    key: ConstantsLagoon.UI_MESSAGING.LEARN_MORE_HYPERLINK_DRAWER_DESCRIPTION,
  });

  const showNetworkInfo = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_PROVIDER_NETWORK_INFO
  );

  const { data: { primaryCare } = {} } = usePrimaryCare();
  const pcpInfo = primaryCare?.find(
    ({ depSeqNbr }) => dependentSeqNbr === depSeqNbr
  )?.pcpInfo;
  const isStaffModel =
    isStaffModelMember ||
    ((enableStaffModel || enableStaffModelEni) &&
      (pcpInfo?.isStaffModel === true ||
        String(pcpInfo?.isStaffModel).toLowerCase() === 'true' ||
        snackCardProvider?.isStaffModel === true ||
        String(snackCardProvider?.isStaffModel).toLowerCase() === 'true'));

  const isStaffModelPcp = isStaffModel && (isPcP || isFuturePcpCard);

  // For staff model PCP/Future PCP cards, use isStaffModelPCPInNetwork to determine
  // clickability since primary care response doesn't have provider details for staff model.
  // When in network, care team provider data is merged and available for navigation.
  const effectiveNetworkStatus = getEffectiveNetworkStatus(
    isStaffModelPcp,
    isStaffModelPCPInNetwork,
    isNetworkStatus
  );

  const getDisplayName = () => {
    if (isStaffModel && isPcP && pcpInfo?.isStaffModel) {
      return pcpInfo?.pcpName || providerName;
    }
    if (isStaffModel && isFuturePcpCard && pcpInfo?.futurePCP?.isStaffModel) {
      return pcpInfo?.futurePCP?.pcpName || providerName;
    }
    return (
      parseProviderName(
        providerName || '',
        providerType,
        professionalDesignation,
        nameAttrs
      ) || providerName || (isPcP && nonGatedPcpProviderName)
    );
  };

  const displayName = getDisplayName();

  const { isKelseySeyboldPlan, isSanitasPlan } = currentMember;
  const kelseySeyboldClinicName = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.KELSEY_SEYBOLD_CLINIC_NAME,
  });

  const kelseySeyboldPcpText = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.KELSEY_SEYBOLD_PCP_TEXT,
  });

  const sanitasClinicName = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.SANITAS_CLINIC_NAME,
  });

  const sanitasPCP = allConfigs()?.find(
    (row) => row.key === 'SANITAS_PCPID'
  )?.value;

  // Use helper function to get external plan member status
  const {
    isKelseySeyboldPlanMember,
    isSanitasPlanMember,
    isExternalPlanMember,
  } = getExternalPlanMemberStatus(
    isKelseySeyboldPlan,
    isSanitasPlan,
    pcpInfo,
    isFuturePcpCard,
    sanitasPCP
  );

  const removeBookmark = useCareTeamStore((state) => state.removeBookmark);
  const { removeCareTeamProvider } = useRemoveCareTeamProvider();
  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );

  const { setDetailsStore } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      setDetailsStore: state.setDetailsStore,
    }))
  );

  const [breadcrumbSessionStorage, setBreadcrumbSessionStorage] =
    useSessionStorage<BreadcrumbSessionStorage>(
      Constants.STORAGE_KEYS.SESSION.BREADCRUMB_URLS,
      {}
    );

  const navigateToSnackDetails = (
    showDirection,
    isTiered?,
    navigateToSectionId?: string
  ) => {
    const detailsStoreValues = {
      specialtySearch: speciality,
      providerId,
      showDirection,
      locationId,
      isTieredSnackCard: isTiered,
      organizationCode,
    };
    const curCovType = coverageType ?? storeCoverageType;
    setStoreCoverageType(curCovType);
    setStoreChipValue(originalChipValue || CareCategories.PRIMARY_CARE);
    setDetailsStore(detailsStoreValues);
    storage.session.set(SELECTED_PROVIDER_ID, providerId);
    const urlParams = convertObjectToUrlParams(chipStore, {
      coverageType: curCovType || 'M',
      providerId,
      showDirection,
      locationId,
      isTieredSnackCard: isTiered,
      llmCodes,
      searchRadius,
      isTieredPlan,
      chipValue: originalChipValue || CareCategories.PRIMARY_CARE,
    });
    const path = getProviderDetailsPath(providerType, urlParams);
    breadcrumbSessionStorage[ConstantsRoutes.CARE_TEAM.key] =
      ConstantsRoutes.CARE_TEAM.path + window.location.search;
    setBreadcrumbSessionStorage(breadcrumbSessionStorage);
    navigate(path, { state: { navigateToSectionId } });
  };

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const {
    setCoverageType: setStoreCoverageType,
    coverageType: storeCoverageType,
    setChipValue: setStoreChipValue,
    setOriginalChipValue: setStoreOriginalChipValue,
  } = chipStore;

  const dataTestId = `get-provider-details-name-${providerType}`.replace(
    / /g,
    '-'
  );
  const providerDetailsCall = 'get-provider-details-call';
  const providerDetailsSchedule = 'get-provider-details-schedule';
  const providerDetailsMiles = 'get-provider-details-miles';
  const providerSpeciality = getProviderSpecialtyOrOrgTypeCode(
    providerType,
    organizationType,
    speciality
  );
  const getLabel = () => `${displayName}${providerSpeciality}`;
  const [showModal, setShowModal] = useState(false);
  const [choosePCP, setChoosePCP] = useState(false);
  const displayThingsToConsider = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_THINGS_TO_CONSIDER_MODAL
  );
  const showThingsToConsider = () =>
    displayThingsToConsider &&
    !(isMnrMember(currentMember.lineOfBusiness) && isPreEffective);

  const openThingsToConsiderModal = () => {
    setShowModal(true);
  };
  const closeThingsToConsiderModal = () => {
    setShowModal(false);
  };
  const handleContinue = () => {
    setShowModal(false);
    choosePCP ? handleChoosePCP() : handleChoosePCPLocations();
  };
  const handleChoosePCPLocations = () => {
    const pcpStoreValues = {
      choosePCPId: providerId,
      dependentSeqNbr,
      selectedProviderType: providerType,
    };
    setPCPSearchState(pcpStoreValues);
    const urlParams = convertObjectToUrlParams(chipStore, pcpStoreValues);
    navigate(`${ConstantsRoutes.CHOOSE_PCP_LOCATION.path}${urlParams}`);
  };
  const handleChoosePCP = () => {
    const urlParams = doChoosePCPStoreUpdate(
      dependentSeqNbr,
      { ...chipStore, chipValue: CareCategories.PRIMARY_CARE },
      setTypeaheadSearchStore,
      setPCPSearchState
    );
    navigate(`${ConstantsRoutes.PROVIDER_SEARCH_RESULTS.path}${urlParams}`);
  };

  const coverage = coverageType ?? storeCoverageType;
  const providerSpecialityTitle = getSpecialityTitle(
    providerType,
    coverage,
    providerSpeciality,
    t
  );

  const specialitiesIds = snackCardProvider?.specialities?.map(
    (item) => item?.id
  );

  const { data: bhSpecialtyCodes = [] } = useSpecialtiesResult({
    coverageType: 'B',
  });
  const bhIcon = getBhIconValue(
    coverage,
    specialitiesIds,
    bhProviderIcons,
    bhSpecialtyCodes,
    getBhIcon
  );

  const networkStatusIcon = getNetworkStatusIcon(isNetworkStatus);

  const isOnlineProvider = getIsOnlineProvider(
    snackCardProvider?.providerLocations
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleLearnMoreClick = () => {
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const onScheduleClick = () => {
    if (isAscensionIndicator && ascensionAnalyticsEnabled) {
      adobeLinkTrackEvent({
        name: AdobeConstantsAscensionSchedule.open_modal,
        location: AdobeConstantsAscensionSchedule.location,
      });
      adobeModalTrackEvent(AdobeConstantsAscensionSchedule.open_modal);
    }
    setIsScheduleOpen(true);
  };

  const networkStatusBadge = (provider) => {
    const msgsOutOfNetwork = t('Network status not available');
    const msgGoingOfOutNetwork = t('Going out-of-network');
    const planList = showNetworkInfo
      ? getPlanIndicator(planInfo, provider.networkId, coverageType)
      : [];
    if (!provider) return '';

    const displayTerminationDateEnabled = getFeatureFlag(
      featureFlags,
      ConstantsLagoon.FEATURE_FLAGS.DISPLAY_FUTURE_TERMINATION_DATE
    );

    const providerLocations = provider?.providerLocations;
    if (!providerLocations || providerLocations.length === 0) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <OutOfNetworkBadge
            isGoingOutOfNetwork={false}
            msg={msgsOutOfNetwork}
            providerName={displayName}
            type="label"
          />
          <LearnMoreHyperLink onLearnMoreClick={handleLearnMoreClick} />
        </div>
      );
    }

    const location = providerLocations[0]?.network;
    const endDate = location?.end;
    const displayFutureTerminationDate = checkTerminationDate(
      displayTerminationDateEnabled,
      endDate
    );

    const isOutOfNetwork = location?.status === 'OON' || !location || (isPcpSuppressed && location?.status === 'NOT_FOUND');
    const isProviderOutOfNetwork =
      provider.isPcp || provider.isFuturePcp
        ? isOutOfNetwork && !futurePCP
        : isOutOfNetwork;
    const isGoingOutOfNetwork =
      location?.status === 'INN' && displayFutureTerminationDate;
    const isProviderGoingOutOfNetwork =
      provider.isPcp || provider.isFuturePcp
        ? isGoingOutOfNetwork && !futurePCP
        : isGoingOutOfNetwork;

    const renderInNetworkBadge = () => (
      <div>
        {planList && planList?.length >= 1 ? (
          planList?.map((planName: string) => (
            <NetworkStatusBadge
              iconColor={networkStatusIcon}
              key={planName}
              name={planName}
              providerId={provider.providerId}
              providerType={provider.providerType}
            />
          ))
        ) : (
          <NetworkStatusBadge
            iconColor={networkStatusIcon}
            name={''}
            providerId={provider.providerId}
            providerType={provider.providerType}
          />
        )}
      </div>
    );

    if (isProviderOutOfNetwork) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <OutOfNetworkBadge
            isGoingOutOfNetwork={false}
            msg={msgsOutOfNetwork}
            providerName={displayName}
            type="label"
          />
          <LearnMoreHyperLink onLearnMoreClick={handleLearnMoreClick} />
        </div>
      );
    } else if (isProviderGoingOutOfNetwork) {
      return (
        <OutOfNetworkBadge
          isGoingOutOfNetwork={true}
          msg={msgGoingOfOutNetwork}
          providerName={displayName}
        />
      );
    }

    return renderInNetworkBadge();
  };

  const onHandleRemoveProvider = () => {
    if (isSuperUser != null) {
      removeCareTeamProvider({
        providerId: snackCardProvider?.providerId,
        isPcp: isPcP,
        onSuccess: () => {
          removeBookmark(snackCardProvider?.providerId);
          removeCallback?.();
          toast.show({
            message: (
              <UndoAction
                data-testid="remove-care-team-action-alert"
                message={removeToastContent?.message}
                providerName={displayName}
              />
            ),
            variant: 'success',
            icon: <IconMaterial color="$white !important" icon="check_circle" />,
            css: toastSuccessStyle,
          });
        },
      });
    }
  };

  const handleProviderDetailsLoad = (data: any, isLoading: boolean) => {
    if (isLoading) return;
    const details =
      data?.facilityDetails?.providerDetails ||
      data?.providerDetails?.providerDetails;

    if (details) {
      setFetchDetails(false);
      setProviderDetails(details);
      navigate(`${ConstantsRoutes.MAP_VIEW.path}?updateLocation=true`, {
        state: {
          selectedLocation: Array.from(
            new Set(
              snackCardProvider?.providerLocations?.map(
                (location) => location.locationId
              )
            )
          ),
          providerId,
          providerType,
          imgSrc,
        },
      });
    }
  };

  const onHandleUpdateLocation = () => {
    const curCovType = coverageType ?? storeCoverageType;
    setStoreCoverageType(curCovType);
    setFetchDetails(true);
    setStoreChipValue(chipValue);
    setStoreOriginalChipValue(originalChipValue);
  };

  // Use helper function to get network status info
  const { isOON, isSuppressed, isOutOfDirectory } = getNetworkStatusInfo(
    snackCardProvider,
    isFuturePcpCard,
    pcpInfo,
    isStaffModel,
    isStaffModelPCPInNetwork,
    nonGatedPcpProviderId
  );

  const disableAllCtas = getDisableAllCtas(
    isPcP,
    isFuturePcpCard,
    isOON,
    isSuppressed
  );

  const menuItems = getMenuItems({
    isPcP,
    isFuturePcpCard,
    isStaffModel,
    showPcpLocation,
    pcpHasSingleLocation,
    isNetworkStatus,
    isOnlineProvider,
    showThingsToConsider,
    setChoosePCP,
    openThingsToConsiderModal,
    handleChoosePCP,
    handleChoosePCPLocations,
    onHandleUpdateLocation,
    onHandleRemoveProvider,
    isPcpSuppressed,
    isSuperUser,
    t,
  });

  const getNetworkBadge = () => {
    if (isPcP || isFuturePcpCard) {
      const providerLocations = snackCardProvider?.providerLocations;
      const location =
        providerLocations && providerLocations.length > 0
          ? providerLocations[0]?.network
          : undefined;
      const isOutOfNetwork = isStaffModel
        ? isStaffModelPCPInNetwork !== undefined && !isStaffModelPCPInNetwork
        : location?.status === 'OON' ||
          location?.status === 'NOT_FOUND' ||
          !location;
      const isProviderOutOfNetwork = isOutOfNetwork;

      const displayTerminationDateEnabled = getFeatureFlag(
        featureFlags,
        ConstantsLagoon.FEATURE_FLAGS.DISPLAY_FUTURE_TERMINATION_DATE
      );

      const endDate = location?.end;
      const displayFutureTerminationDate = checkTerminationDate(
        displayTerminationDateEnabled,
        endDate
      );
      const isGoingOutOfNetwork = isStaffModel
        ? getStaffModelGoingOON(
            providerLocations,
            displayTerminationDateEnabled
          )
        : location?.status === 'INN' && displayFutureTerminationDate;
      const isProviderGoingOutOfNetwork = isGoingOutOfNetwork;

      const planList = showNetworkInfo
        ? getPlanIndicator(planInfo, snackCardProvider?.networkId, coverageType)
        : [];

      if (isOutOfDirectory) {
        return (
          <OutOfNetworkBadge
            isGoingOutOfNetwork={false}
            msg={t('Out-of-network')}
            providerName={displayName}
            type="badge"
          />
        );
      }

      if (isSuppressed) {
        return (
          <NetworkStatusBadge
            iconColor={'$success1'}
            name={''}
            providerId={snackCardProvider?.providerId ?? providerId}
            providerType={snackCardProvider.providerType}
          />
        );
      }

      if (isProviderOutOfNetwork) {
        return (
          <OutOfNetworkBadge
            isGoingOutOfNetwork={false}
            msg={t('Out-of-network')}
            providerName={displayName}
            type="badge"
          />
        );
      }
      if (isProviderGoingOutOfNetwork) {
        return (
          <OutOfNetworkBadge
            isGoingOutOfNetwork={true}
            msg={t('Going out-of-network')}
            providerName={displayName}
            type="badge"
          />
        );
      }
      return (
        <div>
          {planList && planList?.length >= 1 ? (
            planList?.map((planName: string) => (
              <NetworkStatusBadge
                iconColor={networkStatusIcon}
                key={planName}
                name={planName}
                providerId={snackCardProvider?.providerId ?? providerId}
                providerType={snackCardProvider.providerType}
              />
            ))
          ) : (
            <NetworkStatusBadge
              iconColor={networkStatusIcon}
              name={''}
              providerId={snackCardProvider?.providerId ?? providerId}
              providerType={snackCardProvider.providerType}
            />
          )}
        </div>
      );
    }
    return null;
  };
  const sectionType = getSectionType(providerType);

  return (
    <React.Fragment>
      <SnackContainer>
        {isDrawerOpen && (
          <Drawer
            isOpen={isDrawerOpen}
            onClose={handleDrawerClose}
            position="right"
            size="356"
            title={
              <div
                data-testid="learn-more-drawer-title"
                style={{ marginTop: '16px' }}
              >
                {learnMoreDrawerTitle?.message}
              </div>
            }
          >
            <LearnMoreDrawer>
              <Markdown data-testid="learn-more-drawer-description">
                {learnMoreDrawerDescription?.message}
              </Markdown>
            </LearnMoreDrawer>
          </Drawer>
        )}
        <ProviderInfoCard
          alignContent="center"
          alignItems="center"
          data-auto-testid={`snack-card-${providerType}-${providerId}`}
          data-testid={`snack-card-${providerType}-${providerId}`}
          direction="column"
        >
          <ProviderCardContainer
            data-auto-testid="snack-clickable-container"
            data-testid="snack-clickable-container"
            isNetworkStatus={isPcpAndExternalPlanmemberAndNetworkStatus(
              effectiveNetworkStatus,
              isPcP,
              isKelseySeyboldPlanMember,
              isSanitasPlanMember,
              isFuturePcpCard
            )}
            onClick={() => {
              isPcpAndExternalPlanmemberAndNetworkStatus(
                effectiveNetworkStatus,
                isPcP,
                isKelseySeyboldPlanMember,
                isSanitasPlanMember,
                isFuturePcpCard
              ) && navigateToSnackDetails(false, isTieredProvider);
            }}
          >
            <ProviderCardHeader
              isExternalPlanMember={isExternalPlanMember}
              isFuturePcpCard={isFuturePcpCard}
              isPcP={isPcP}
              menuItems={menuItems}
              pcpEffectiveEndDate={pcpEffectiveEndDate}
              pcpEffectiveStartDate={pcpEffectiveStartDate}
              showChooseOrEditPcpCTA={pcpConfig?.showChooseOrEditPcpCTA}
              showEffectiveEndDate={showEffectiveEndDate}
              showEffectiveStartDate={showEffectiveStartDate}
            />
            <Avatar
              data-testid={`snack-card-${providerType}-profile`.replace(
                / /g,
                '-'
              )}
              style={{
                backgroundColor: !effectiveNetworkStatus
                  ? '#F3F3F3'
                  : undefined,
              }}
            >
              {(isPcP || isFuturePcpCard) &&
              isStaffModel &&
              effectiveNetworkStatus ? (
                <IconBrandAndSymbol icon={IconName.clinic} size={128} />
              ) : isPcpAndExternalPlanmember(
                  isPcP,
                  isKelseySeyboldPlanMember,
                  isSanitasPlanMember
                ) ||
                (isFuturePcpCard && isExternalPlanMember) ? (
                <IconBrand icon="network" size={'128px'} />
              ) : effectiveNetworkStatus ? (
                ImageComponent({
                  imgRef,
                  providerType,
                  imgSrc,
                  bhIcon,
                  sizeToken: '128px',
                  expandedProviderListView: false,
                  ...additionalIconCss,
                })
              ) : (
                <IconSymbol size="40px" />
              )}
            </Avatar>
            <SnackDescription
              alignContent="center"
              alignItems="center"
              direction="column"
              justify="space-between"
            >
              <Tooltip content={displayName}>
                <Text
                  color="$accent1"
                  data-auto-testid={dataTestId}
                  data-testid={dataTestId}
                  size="$lg"
                >
                  <LinkWithTracking
                    aria-label={getLabel()}
                    css={providerNetworkStatusCss(effectiveNetworkStatus)}
                    data-auto-testid={`${dataTestId}-link`}
                    data-testid={`${dataTestId}-link`}
                    isDisabled={
                      isStaffModel
                        ? !effectiveNetworkStatus
                        : !isPcpAndExternalPlanmemberAndNetworkStatus(
                            isNetworkStatus,
                            isPcP,
                            isKelseySeyboldPlanMember,
                            isSanitasPlanMember,
                            isFuturePcpCard
                          )
                    }
                    isStandardAnchor
                  >
                    {displayName}
                  </LinkWithTracking>
                </Text>
              </Tooltip>
              {isPcpAndExternalPlanmemberAndNetworkStatus(
                effectiveNetworkStatus,
                isPcP,
                isKelseySeyboldPlanMember,
                isSanitasPlanMember,
                isFuturePcpCard
              ) && !isStaffModelPcp ? (
                <Tooltip content={providerSpecialityTitle}>
                  <Text
                    color="$gray8"
                    css={{ marginTop: '20px' }}
                    data-auto-testid="get-provider-details-role"
                    data-testid="get-provider-details-role"
                    size="$md"
                  >
                    {truncateLargeStrings(providerSpecialityTitle, 30)}
                  </Text>
                </Tooltip>
              ) : (
                <HiddenSpecialityDiv />
              )}
              {(isPcP && isKelseySeyboldPlanMember) ||
              (isFuturePcpCard && isKelseySeyboldPlanMember) ? (
                <Text
                  color="$gray8"
                  css={{
                    marginBottom: '16px',
                    marginLeft: '8px',
                    marginRight: '8px',
                  }}
                  data-auto-testid="kelsey-seybold-pcp-text"
                  data-testid="kelsey-seybold-pcp-text"
                  size="$sm"
                >
                  {kelseySeyboldPcpText?.message}
                </Text>
              ) : null}
              {!isPcpAndExternalPlanmember(
                isPcP,
                isKelseySeyboldPlanMember,
                isSanitasPlanMember
              ) &&
                !(isFuturePcpCard && isExternalPlanMember) && (
                  <NetworkWrapper
                    data-auto-testid="in-network-badge-container"
                    data-testid="in-network-badge-container"
                    style={{ padding: '10px' }}
                  >
                    {(isPcP || isFuturePcpCard) && !isPcpSuppressed
                      ? getNetworkBadge()
                      : networkStatusBadge(snackCardProvider)}
                  </NetworkWrapper>
                )}
            </SnackDescription>
            <CTAContainer data-testid="cta-container">
              {renderCtaButtons({
                isPcP: isPcP && !isPcpSuppressed,
                isFuturePcpCard,
                isExternalPlanMember,
                isKelseySeyboldPlanMember,
                isSanitasPlanMember,
                displayName,
                kelseySeyboldClinicName,
                sanitasClinicName,
                isNetworkStatus: effectiveNetworkStatus,
                navigateToSnackDetails,
                isTieredProvider,
                isOnlineProvider,
                disableAllCtas,
                providerDetailsMiles,
                setProviderDetails,
                snackCardProvider,
                navigate,
                ConstantsRoutes,
                providerDetailsCall,
                setIsOpen,
                isDocAsapOrScheduleIndicator,
                providerDetailsSchedule,
                onScheduleClick,
                t,
                pcpConfig,
                isStaffModel,
              })}
            </CTAContainer>
          </ProviderCardContainer>
        </ProviderInfoCard>
        {isOpen ? (
          <CallModal
            data={snackCardProvider?.providerLocations ?? []}
            isOpen={isOpen}
            onClose={() => {
              setIsOpen(false);
            }}
          />
        ) : null}
        {isScheduleOpen &&
          (isAscensionIndicator ? (
            <AscensionScheduleModal
              ascensionIndicator={isAscensionIndicator}
              ascensionSchedulingLink={ascensionSchedulingLink}
              isOpen={isScheduleOpen}
              isTieredDataCard={false}
              locationId={locationId || ''}
              npi={snackCardProvider?.npi}
              phone={phone}
              practiceId={practiceId}
              providerId={snackCardProvider?.providerId}
              providerName={displayName || ''}
              sectionType={sectionType}
              setIsOpen={setIsScheduleOpen}
              showDirection={false}
            />
          ) : (
            <ScheduleModal
              isOpen={isScheduleOpen}
              isTieredDataCard={false}
              npi={snackCardProvider?.npi}
              onClose={() => {
                setIsScheduleOpen(false);
              }}
              providerId={snackCardProvider?.providerId}
              providerLocations={snackCardProvider?.providerLocations ?? []}
              providerType={snackCardProvider?.providerType}
            />
          ))}
        {showModal && (
          <ThingsToConsiderModal
            editPcpClicked={false}
            isOpen={showModal}
            onClose={closeThingsToConsiderModal}
            onContinue={handleContinue}
          />
        )}
      </SnackContainer>
      {fetchDetails && (
        <ProviderDetailsLoader
          onLoad={handleProviderDetailsLoad}
          providerId={snackCardProvider?.providerId}
          providerType={snackCardProvider?.providerType}
        />
      )}
    </React.Fragment>
  );
};

const getMenuItems = ({
  isPcP,
  isFuturePcpCard,
  isStaffModel,
  showPcpLocation,
  pcpHasSingleLocation,
  isNetworkStatus,
  isOnlineProvider,
  showThingsToConsider,
  setChoosePCP,
  openThingsToConsiderModal,
  handleChoosePCP,
  handleChoosePCPLocations,
  onHandleUpdateLocation,
  onHandleRemoveProvider,
  isPcpSuppressed,
  isSuperUser,
  t,
}: any) => {
  // For non-gated members with suppressed PCP, show only "Remove from care team"
  if (isPcpSuppressed && isPcP) {
    return showRemoveLocOption(false, t, onHandleRemoveProvider, isSuperUser, true);
  }

  return isPcP || isFuturePcpCard
    ? [
        {
          title: t('PRIMARY_CARE_PROVIDER.CHANGE_YOUR_PCP'),
          onClick: () => {
            if (showThingsToConsider()) {
              setChoosePCP(true);
              openThingsToConsiderModal();
            } else {
              handleChoosePCP();
            }
            adobeLinkTrackEvent({
              name: ANALYTICS_CONSTANTS.CHANGE_YOUR_PCP,
              location: `${ANALYTICS_CONSTANTS.EDIT_PCP_DROPDOWN}:${ANALYTICS_CONSTANTS.PCP_SNACK_CARD}`,
              type: ANALYTICS_CONSTANTS.INTERNAL_TYPE,
            });
          },
          disabled: false,
        },
        ...(!isStaffModel && showPcpLocation && !pcpHasSingleLocation
          ? [
              {
                title: t('PRIMARY_CARE_PROVIDER.CHANGE_YOUR_PCP_LOCATION'),
                onClick: () => {
                  showPcpLocation
                    ? showThingsToConsider()
                      ? openThingsToConsiderModal()
                      : handleChoosePCPLocations()
                    : onHandleUpdateLocation();
                  adobeLinkTrackEvent({
                    name: ANALYTICS_CONSTANTS.CHANGE_YOUR_PCP_LOCATION,
                    location: `${ANALYTICS_CONSTANTS.EDIT_PCP_DROPDOWN}:${ANALYTICS_CONSTANTS.PCP_SNACK_CARD}`,
                    type: ANALYTICS_CONSTANTS.INTERNAL_TYPE,
                  });
                },
                disabled: !isNetworkStatus,
              },
            ]
          : []),
      ]
    : [
        ...(isNetworkStatus && !isOnlineProvider
          ? [
              {
                title: t('CARE_TEAM.PROVIDER_EDIT_DROPDOWN.UPDATE_LOCATION'),
                onClick: () => {
                  isPcP
                    ? showThingsToConsider()
                      ? openThingsToConsiderModal()
                      : handleChoosePCPLocations()
                    : onHandleUpdateLocation();
                },
                disabled: false,
                'data-testid': 'update-location',
              },
            ]
          : []),
        ...showRemoveLocOption(isPcP, t, onHandleRemoveProvider, isSuperUser),
      ];
};

const showRemoveLocOption = (
  isPcP: boolean,
  t,
  onHandleRemoveProvider: () => void,
  isSuperUser?: boolean,
  isNonGatedSuppressedPcp?: boolean
) =>
  !isPcP
    ? [
        {
          title: t('CARE_TEAM.PROVIDER_EDIT_DROPDOWN.REMOVE_FROM_CARE_TEAM'),
          onClick: (event: React.MouseEvent) => {
            event.stopPropagation();
            onHandleRemoveProvider();
          },
          disabled: isNonGatedSuppressedPcp && isSuperUser,
          'data-testid': 'remove-from-care-team',
        },
      ]
    : [];

const isPcpAndExternalPlanmember = (
  isPcP: boolean,
  isKelseySeyboldPlanMember: any,
  isSanitasPlanMember: any
) => isPcP && (isKelseySeyboldPlanMember || isSanitasPlanMember);

const providerNetworkStatusCss = (isNetworkStatus: boolean) => ({
  color: isNetworkStatus ? '$accent1' : '#000',
  justifyContent: 'flex-end',
  cursor: isNetworkStatus ? 'pointer' : 'not-allowed',
  marginTop: isNetworkStatus ? 0 : '10px',
});
